import React, { SyntheticEvent, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {
  doOptionsContainValue,
  getLabelFromOptionValues,
  Option,
} from '@/components/Filter/FilterRow/FilterRow.tsx';
import {
  FilterRowSelection,
  FilterRowSelectionValue,
} from '@lambda-layer/types/filter.ts';
import { FilterDefinition } from '/opt/nodejs/models/FilterDefinition/FilterDefinition.ts';
import { useMessageQueue } from '@erickson/components';

export interface Props {
  /** The complete filter definition from which label, labelMapping, and isMetaField are derived */
  filterDefinition: FilterDefinition;
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowValue: FilterRowSelection) => void;
  /**
   * The complete data set of options.
   */
  options: Option[];
  onFocusChange?: () => void;
  isLoading?: boolean;

  /**
   * Regex used to split the user's pasted/typed input.
   * Defaults to splitting on commas or any whitespace.
   */
  delimiters?: RegExp;
  autoFocus?: boolean;
}

/**
 * This component works in two distinct modes:
 *
 * 1. **Infinite Scroll Mode (when searchText is empty):**
 *    - Uses paginated data (with a header) so that performance isn’t impacted for very large lists.
 * 2. **Search Mode (when searchText is non‑empty):**
 *    - Filters the complete data set (case‑insensitive “contains”) so that every matching option is shown.
 *
 * In addition, when the user pastes or types data that contains delimiters the input is tokenized,
 * validated against the complete data set, and merged with any existing selections.
 *
 * **Key Point:** We separate the search text from the selected tokens. The Autocomplete’s
 * `inputValue` is controlled by `searchText` and the `value` prop by `filterRowSelection.value`.
 */
const FilterRowSearchableMultiSelect: React.FC<Props> = ({
  filterDefinition,
  filterRowSelection,
  onChange,
  options = [],
  onFocusChange,
  isLoading = false,
  delimiters = /[\s,]+/,
  autoFocus = false,
}) => {
  const messageQueue = useMessageQueue();

  const { labelMapping, isDatabaseField } = filterDefinition;
  // Compute whether this is a meta field.
  const isMetaField = !isDatabaseField;

  const [open, setOpen] = useState(false);

  // Controls how many options are loaded when not searching.
  const [resultLimit, setResultLimit] = useState(25);
  // Holds the current text entered by the user.
  const [searchText, setSearchText] = useState('');

  // When the user selects an option (or multiple) from the dropdown,
  // filter the values using the full data set.
  const handleSelectChange = (
    _: SyntheticEvent<Element, Event>,
    values: FilterRowSelectionValue[]
  ) => {
    if (!Array.isArray(values)) {
      throw new Error('Expected an array value.');
    }
    // Filter out any free text values that aren’t valid options.
    const validValues = values.filter(value =>
      options.some(option => option.value === value)
    );
    const updatedValues: FilterRowSelection = {
      ...filterRowSelection,
      value: Array.from(new Set(validValues)), // remove duplicates
    };
    onChange(updatedValues);
  };

  // For normal typing, update the search text.
  const handleSearchInputChange = (
    _: SyntheticEvent<Element, Event>,
    value: string
  ) => {
    setSearchText(value);
  };

  /**
   * Processes tokens from a given input string.
   * Splits the input using the provided delimiters and validates each token.
   *
   * @param input The input string to process.
   * @returns An error message if any tokens are invalid, otherwise null.
   */
  const processTokens = (input: string): string | null => {
    const tokens = input
      .split(delimiters)
      .map(token => token.trim())
      .filter(Boolean);

    let errorMessage: string | null = null;
    if (tokens.length) {
      // Separate valid tokens from invalid tokens.
      const validTokens = tokens.filter(token =>
        options.some(
          option => option.value?.toLowerCase() === token.toLowerCase()
        )
      );
      const invalidTokens = tokens.filter(
        token =>
          !options.some(
            option => option.value?.toLowerCase() === token.toLowerCase()
          )
      );

      // Add valid tokens as chips.
      if (validTokens.length > 0) {
        const newSelections = Array.from(
          new Set([...filterRowSelection.value, ...validTokens])
        );
        onChange({ ...filterRowSelection, value: newSelections });
      }

      // If there are invalid tokens, construct an error message
      // and display it via the message queue.
      if (invalidTokens.length > 0) {
        errorMessage = `Invalid value${
          invalidTokens.length > 1 ? 's' : ''
        }: ${invalidTokens.join(', ')}`;
        messageQueue.addMessage({
          text: errorMessage,
          severity: 'error',
        });
      }
      // Clear the input field.
      setSearchText('');
    }
    return errorMessage;
  };

  // When a paste occurs, process the tokens from the pasted text.
  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    event.preventDefault();
    const pasteData = event.clipboardData.getData('Text');
    processTokens(pasteData);
  };

  // When a delimiter is typed, process tokens.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === ',' || event.key === ' ') {
      event.preventDefault();
      if (searchText.trim() === '') return;
      processTokens(searchText);
    }
  };

  // Compute the options to display.
  const displayOptions = useMemo(() => {
    // Do not clear options while loading; keep the list stable to avoid flicker/close
    if (searchText.trim() !== '') {
      return options
        .filter(option =>
          option.label?.toLowerCase().includes(searchText.toLowerCase())
        )
        .map(option => option.value);
    }
    return [
      '$HEADER',
      ...options.slice(0, resultLimit).map(option => option.value),
    ];
  }, [searchText, options, resultLimit]);

  const renderLoadingIndicator = (
    <MenuItem disabled>
      <CircularProgress size={20} />
      <Typography ml={2}>Loading...</Typography>
    </MenuItem>
  );

  return (
    <Autocomplete
      autoFocus={autoFocus}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      freeSolo
      multiple
      disableCloseOnSelect
      value={
        doOptionsContainValue(options, filterRowSelection.value)
          ? filterRowSelection.value.map(value => value?.toString() || '')
          : []
      }
      // Always provide options to keep menu stable; show a light updating indicator when loading
      options={displayOptions}
      getOptionLabel={value => getLabelFromOptionValues(labelMapping, [value])}
      onChange={handleSelectChange}
      // Use a controlled input so that we can update the search results immediately.
      onInputChange={handleSearchInputChange}
      inputValue={searchText}
      onFocus={() => onFocusChange?.()}
      onBlur={() => onFocusChange?.()}
      size="small"
      loading={isLoading}
      loadingText={renderLoadingIndicator}
      renderOption={(props, value, { selected }) => {
        const { key, ...optionProps } = props;
        if (value === '$HEADER') {
          return (
            <Box
              key={key}
              sx={{
                position: 'relative',
                minHeight: 35,
                bgcolor: theme => theme.palette.grey.A200,
              }}
            >
              <IconButton
                size="small"
                aria-label="close"
                sx={{
                  position: 'absolute',
                  right: 3,
                  top: 3,
                  color: theme => theme.palette.grey[500],
                }}
              >
                <CloseIcon fontSize="inherit" />
              </IconButton>
              {/* Inline updating indicator (non-blocking) */}
              {isLoading && open && (
                <Box
                  sx={{
                    position: 'absolute',
                    left: 8,
                    top: 6,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    color: theme => theme.palette.grey[600],
                  }}
                >
                  <CircularProgress size={14} />
                  <Typography variant="caption">Updating…</Typography>
                </Box>
              )}
            </Box>
          );
        } else {
          return (
            <li key={key} {...optionProps}>
              <Checkbox checked={selected} />
              {getLabelFromOptionValues(labelMapping, [value])}{' '}
              {!isMetaField &&
                '(' + options.find(o => o.value === value)?.count + ')'}
            </li>
          );
        }
      }}
      renderInput={params => (
        <TextField
          {...params}
          onPaste={handlePaste}
          onKeyDown={handleKeyDown}
          onBlur={event => {
            if (searchText.trim()) {
              processTokens(searchText);
            }
            if (params.inputProps.onBlur) {
              params.inputProps.onBlur(
                event as React.FocusEvent<HTMLInputElement>
              );
            }
          }}
        />
      )}
      slotProps={{
        listbox: {
          onScroll: event => {
            if (searchText.trim() !== '') return;
            const listboxNode = event.currentTarget;
            if (
              listboxNode.scrollTop + listboxNode.clientHeight + 0.5 >=
              listboxNode.scrollHeight
            ) {
              setResultLimit(prevLimit => prevLimit + 25);
            }
          },
        },
      }}
    />
  );
};

export default FilterRowSearchableMultiSelect;
